import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  ButtonBase,
  Container,
  Grid,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextFieldProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import {
  getCoursesState,
  resetAdminCoursesData,
  getCoursesForAdmin,
  loadMoreCoursesForAdmin,
} from '../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import { TPTextField } from '../../../../components/Inputs';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import CourseCard from './components/CourseCard';
import CreateCourseOptionsModal from './modals/CreateCourseOptionsModal';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { checkUserPermission } from '../../../../helper';
import { getAuthState } from '../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/UserSlice';

type TState = {
  filterData: { limit: number; offset: number };
  searchText: string;
  anchorEl: HTMLElement | null;
  isCreateCourseOptionsModalOpen: boolean;
};

const searchInputProps: TextFieldProps = {
  fullWidth: true,
  placeholder: 'Search Courses',
  sx: {
    maxWidth: '666px',
  },
  InputProps: {
    sx: {
      textAlign: 'center',
      backgroundColor: TP_COLORS.white,
      borderRadius: '35px',
      boxShadow: '0px 14px 40px 0px #6E9DB22E',
    },
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
  },
};

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  options.push({
    type: 'text',
    title: 'Courses',
    highlight: true,
  });

  return options;
};

const initialFilterData: TState['filterData'] = {
  limit: 30,
  offset: 0,
};

const initialState: TState = {
  filterData: { ...initialFilterData },
  searchText: '',
  anchorEl: null,
  isCreateCourseOptionsModalOpen: false,
};

const AdminCourses: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);

  const { adminCoursesData } = coursesState;
  const isMoreOptionsMenuOpen = Boolean(state.anchorEl);
  const breadcrumbOptions = constructBreadcrumbOptions();

  useEffect(() => {
    dispatch(
      getCoursesForAdmin({
        ...state.filterData,
        searchText: state.searchText,
      })
    );

    return () => {
      dispatch(resetAdminCoursesData());
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
      filterData: { ...initialFilterData },
    }));

    dispatch(
      getCoursesForAdmin({
        ...initialFilterData,
        searchText: value,
      })
    );
  };

  const openMoreOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setState((prevState) => ({
      ...prevState,
      anchorEl: event.currentTarget,
    }));
  };

  const closeMoreOptionsMenu = () => {
    setState((prevState) => ({
      ...prevState,
      anchorEl: null,
    }));
  };

  const toggleCreateCourseOptionsModal = () => {
    setState((prevState) => ({
      ...prevState,
      isCreateCourseOptionsModalOpen: !prevState.isCreateCourseOptionsModalOpen,
    }));
  };

  const handleLoadMore = () => {
    if (adminCoursesData) {
      const newFilterData = {
        ...state.filterData,
        offset: adminCoursesData.list.length,
      };

      setState((prevState) => ({
        ...prevState,
        filterData: newFilterData,
      }));

      dispatch(loadMoreCoursesForAdmin(newFilterData));
    }
  };

  const moreOptionsMenuOptions = [
    {
      icon: AddIcon,
      title: 'Add New Course',
      onClick: () => {
        toggleCreateCourseOptionsModal();
        closeMoreOptionsMenu();
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          background: `url('${process.env.PUBLIC_URL}/images/backgrounds/bg-courses-grid.png') 0px -15px`,
          paddingBottom: '90px',
        }}
      >
        <Container maxWidth="lg">
          <div style={{ padding: `30px 0 ${isXsScreen ? '15px' : '0'} 0` }}>
            <Breadcrumbs
              options={breadcrumbOptions}
              textColor="#647488"
              showDropDown={isXsScreen}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '15px' }}>
              <Typography
                variant={isXsScreen ? 'h2' : 'h1'}
                color="#006C9C"
                fontWeight={800}
              >
                Courses
              </Typography>
            </div>
            <Stack
              direction="row"
              justifyContent="center"
              style={{ marginBottom: '45px' }}
            >
              <Typography color="#006C9C" fontWeight={500} maxWidth="750px">
                Meet our incredible team of experts with decades of experience!
                They&apos;re here to make your academic journey a success,
                offering guidance and support along the way.
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="center">
              <TPTextField
                {...searchInputProps}
                name="searchText"
                value={state.searchText}
                onChange={handleInputChange}
              />
            </Stack>
          </div>
        </Container>
      </Box>
      <Container maxWidth="lg" style={{ marginTop: '-45px' }}>
        <Stack alignItems="flex-end">
          <Tooltip title="Options" placement="top">
            <ButtonBase
              id="more-options-button"
              aria-haspopup="true"
              onClick={openMoreOptionsMenu}
              aria-expanded={isMoreOptionsMenuOpen ? 'true' : undefined}
              aria-controls={
                isMoreOptionsMenuOpen ? 'more-options-menu' : undefined
              }
              sx={{
                backgroundColor: '#F1F5F9',
                padding: '5px',
                borderRadius: '5px',
              }}
            >
              <MoreVertIcon />
            </ButtonBase>
          </Tooltip>
        </Stack>
        <Grid container spacing={2}>
          {!adminCoursesData && coursesState.status === 'loading' ? (
            <Grid item xs={12}>
              <BasicLoader />
            </Grid>
          ) : null}
          {adminCoursesData && (adminCoursesData.list || []).length
            ? adminCoursesData.list.map((item) => {
                return (
                  <Grid
                    key={`admin-course-${item._id}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                  >
                    <CourseCard course={item} />
                  </Grid>
                );
              })
            : null}
          {adminCoursesData &&
            (adminCoursesData.totalDocs || 0) >
              adminCoursesData.list.length && (
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    loading={coursesState.status === 'loading'}
                    disabled={coursesState.status === 'loading'}
                    onClick={handleLoadMore}
                  >
                    Load More
                  </LoadingButton>
                </Stack>
              </Grid>
            )}
          {adminCoursesData &&
          adminCoursesData.list &&
          adminCoursesData.list.length < 1 &&
          coursesState.status === 'idle' ? (
            <Grid item xs={12}>
              <Typography variant="h6" textAlign="center">
                No courses found
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </Container>
      {/* Modals */}
      {checkUserPermission(authState.user, {
        adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE],
      }) && (
        <>
          <Menu
            open={isMoreOptionsMenuOpen}
            id="more-options-menu"
            anchorEl={state.anchorEl}
            onClose={closeMoreOptionsMenu}
            MenuListProps={{
              'aria-labelledby': 'more-options-button',
            }}
          >
            {moreOptionsMenuOptions.map((item, index) => {
              const Icon = item.icon;

              return (
                <MenuItem
                  key={`more-option-menu-li-${index + 1}`}
                  onClick={item.onClick}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText>{item.title}</ListItemText>
                </MenuItem>
              );
            })}
          </Menu>
          <CreateCourseOptionsModal
            open={state.isCreateCourseOptionsModalOpen}
            onClose={toggleCreateCourseOptionsModal}
          />
        </>
      )}
    </>
  );
};

export default AdminCourses;
